import React, { ChangeEvent, FC, useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'

import MainModal from './common/MainModal'
import { CustomScoringModalSection } from './components/Operations/CustomScoringModalSection/CustomScoringModalSection'
import { CustomScoringModalHeader } from './components/Operations/CustomScoringModalHeader/CustomScoringModalHeader'
import { FurtherRemarksInput } from './components/Operations/FurtherRemarksInput/FurtherRemarksInput'
import { ScoringRiderInfo } from './components/Operations/ScoringRiderInfo/ScoringRiderInfo'
import { FenceHeight } from './components/Operations/FenceHeight/FenceHeight'
import { JudgesSignature } from './components/Operations/JudgesSignature/JudgesSignature'

import useToasterHelper from '../../helpers/ToasterHelper'
import { generateName } from '../../helpers/helpers'
import { handleImageError } from '../../helpers/handleImageError'
import { getRegisteredRiders } from '../../helpers/riders'
import { sortByTotalPoints } from '../../pages/organizer/manage/clinic/ManageClinicNOtherOperationsTab/data/operations-tab-utils'

import FirestoreService from '../../services/firestoreService'

import {
  IScoreCardInterface,
  IScoreCardSection,
} from '../../models/score-cards/score-cards.interface'
import {
  IRegistrationByDayInterface,
  ISheetSection,
} from '../../models/registrations-by-day/registrationByDay.interface'

import { RegistrationByDayModel } from '../../models/registrations-by-day/registrationByDay.model'

import { MODAL_CONSTS } from '../../const/modal-const'
import { CONST } from '../../const/const'
import { MESSAGES_CONST } from '../../const/messages-const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

interface CustomScoringModalProps {
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn: {
    modalTitle?: string
    editable: boolean
    onSaveScoring?: () => Promise<void>
    rider: IRegistrationByDayInterface
    type?: 'event-results'
  }
}

export const CustomScoringModal: FC<CustomScoringModalProps> = ({ handleModal, dataToPassOn }) => {
  const { modalTitle, editable, rider } = dataToPassOn
  const toastFunctions = useToasterHelper()

  const [file, setFile] = useState<string | null>(rider?.score?.file ?? null)
  const [row, setRow] = useState<IRegistrationByDayInterface>(rider ?? {})
  const [loading, setLoading] = useState(false)
  const [isLocked, setIsLocked] = useState(false)

  const {
    handleSubmit,
    watch,
    register,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm<IScoreCardInterface>({
    defaultValues: {
      id: '',
      name: '',
      furtherRemarks: '',
      sections: [],
      isWithdrew: false,
      showJumpOff: false,
      fenceHeight: '',
    },
    mode: 'onChange',
  })

  const onSubmit = async () => {
    setLoading(true)

    const registrationsByDay = await getRegisteredRiders([dataToPassOn.rider.eventId ?? ''])

    let registrationsByDayUpdated: IRegistrationByDayInterface[] =
      registrationsByDay?.map((registrationByDay) => {
        if (registrationByDay.id === row.id) {
          let totalPoints = 0
          if (!watch('isWithdrew')) {
            watch('sections').forEach((section) => {
              totalPoints += Number(section.totalTime) ?? 0
            })
          }

          return {
            ...registrationByDay,
            score: {
              ...row.score,
              judges: [
                {
                  id: dataToPassOn.rider.score?.judges?.[0]?.id ?? '',
                  name: dataToPassOn.rider.score?.judges?.[0]?.name ?? '',
                  picture: dataToPassOn.rider.score?.judges?.[0]?.picture ?? '',
                  sections: [],
                  customSections: watch('sections'),
                  furtherRemarks: watch('furtherRemarks'),
                  isWithdrew: watch('isWithdrew'),
                  showJumpOff: watch('showJumpOff'),
                  fenceHeight: watch('fenceHeight'),
                  signatureUrl: dataToPassOn.rider.score?.judges?.[0]?.signatureUrl ?? null,
                  signatureDate: dataToPassOn.rider.score?.judges?.[0]?.signatureDate ?? null,
                },
              ],
              totalPoints,
              file: file ?? null,
            },
          }
        } else {
          return registrationByDay
        }
      }) ?? []

    let sortedRegistrations = sortByTotalPoints(registrationsByDayUpdated)

    let count = 0

    // update rank for all registrations that have the same class
    sortedRegistrations = sortedRegistrations?.map((registrationByDay) => {
      if (dataToPassOn.rider.uuid === registrationByDay.uuid) {
        count++
        return {
          ...registrationByDay,
          ...(registrationByDay.score && {
            score: { ...registrationByDay.score, rank: count },
          }),
        }
      } else {
        return registrationByDay
      }
    })

    // Update firestore collection
    if (sortedRegistrations?.length) {
      await Promise.all(
        sortedRegistrations.map(async (registrationByDay) => {
          const item = new RegistrationByDayModel(registrationByDay)
          await FirestoreService.updateItem(
            COLLECTIONS.REGISTRATION_BY_DAY.NAME,
            item.id,
            item.toFirestore()
          )
        })
      )
    }

    if (dataToPassOn?.onSaveScoring) await dataToPassOn.onSaveScoring()

    toastFunctions.success({ message: MESSAGES_CONST.EVENT_SCORE_UPDATED })
    setLoading(false)
    handleModal(false, MODAL_CONSTS.CUSTOM_SCORING)
  }

  useEffect(() => {
    if (rider.score) {
      let sections: ISheetSection[] | IScoreCardSection[] = []

      if (rider.score.judges?.[0]?.sections?.[0]) {
        sections = rider.score.judges?.[0]?.sections
      }

      if (rider.score.judges?.[0]?.customSections?.[0]) {
        sections = rider.score.judges?.[0]?.customSections
      }

      reset({
        name: rider.score.sheet?.name,
        furtherRemarks: rider.score.judges?.[0]?.furtherRemarks ?? '',
        sections,
        isWithdrew: rider.score.judges?.[0]?.isWithdrew ?? false,
        showJumpOff: rider.score.judges?.[0]?.showJumpOff ?? false,
        fenceHeight: rider.score.judges?.[0]?.fenceHeight ?? '',
      })
    }
  }, [rider.score])

  const showLockedModal = () => {
    handleModal(true, MODAL_CONSTS.RESULTS_LOCKED, {
      ...dataToPassOn,
      setIsLocked,
    })
  }

  useEffect(() => {
    if (Number(row.score?.totalPoints) > 0) {
      setIsLocked(true)
    }
  }, [row.score?.totalPoints])

  const onChangeFurtherRemarks = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (isLocked) showLockedModal()
    else setValue('furtherRemarks', e.target.value)
  }

  return (
    <MainModal
      title={modalTitle}
      titleClassName="font-bold"
      size="xl"
      modalClassName={'!z-[50]'}
      overlayClassName={'!z-[40]'}
      className="!px-0"
      type="CUSTOM_SCORING"
      show={true}
      buttons={[
        {
          hidden: !editable,
          loading,
          label: 'Save & Lock',
          fullWidth: true,
          onClick: (e) => handleSubmit(onSubmit)(e),
        },
        {
          label: 'Cancel',
          fullWidth: true,
          className: 'outline-none',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor',
          onClick: () => handleModal(false, MODAL_CONSTS.CUSTOM_SCORING),
        },
      ]}
    >
      <div className="text-SeabiscuitDark200ThemeColor">
        {row?.id && (
          <ScoringRiderInfo
            row={row}
            setRow={setRow}
            setFile={setFile}
            file={file}
            editable={dataToPassOn.editable ?? false}
            className="pb-6 border-b border-b-SeabiscuitLightThemeColorD3"
          />
        )}

        {rider.score?.judges?.[0] ? (
          <>
            <CustomScoringModalHeader
              sections={watch('sections')}
              watch={watch}
              setValue={setValue}
              editable={dataToPassOn.editable ?? false}
              type={dataToPassOn.type}
              rider={rider}
            />
            {watch('fenceHeight') && (
              <div className="pb-6 mb-6 border-b border-b-SeabiscuitLightThemeColorD3">
                <FenceHeight
                  className="py-3 px-3 bg-SeabiscuitGrayThemeColor rounded-md"
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  trigger={trigger}
                  editable={false}
                />
              </div>
            )}
            {!watch('isWithdrew') && (
              <div className="border border-SeabiscuitLightThemeColorD3 rounded-md p-2">
                <div className="flex items-center gap-2">
                  <img
                    className="w-[45px] h-[45px] rounded-md object-cover"
                    src={rider.score?.judges?.[0]?.picture ?? ''}
                    onError={(e) =>
                      handleImageError(e, generateName(rider.score?.judges?.[0]?.name || ''))
                    }
                    alt=""
                  />
                  <div>
                    <p className="text-[12px] text-SeabiscuitDark200ThemeColor/50">Judge</p>
                    <p>{rider.score?.judges?.[0]?.name}</p>
                  </div>
                </div>
                {watch('sections')?.map((section, sectionIndex) => (
                  <CustomScoringModalSection
                    key={section.id}
                    section={section}
                    sectionIndex={sectionIndex}
                    setValue={setValue}
                    watch={watch}
                    register={register}
                    isLocked={isLocked}
                    showLockedModal={showLockedModal}
                    editable={dataToPassOn.editable ?? false}
                  />
                ))}
                <FurtherRemarksInput
                  editable={editable}
                  furtherRemarksValue={watch('furtherRemarks')}
                  onChangeFurtherRemarks={onChangeFurtherRemarks}
                />
                {editable && (
                  <JudgesSignature
                    showLockedModal={showLockedModal}
                    isLocked={isLocked}
                    row={row}
                    setRow={setRow}
                    judgeIndex={0}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <p className="opacity-50 p-4 text-center">Please select a judge first</p>
        )}
      </div>
    </MainModal>
  )
}
